import { Helmet } from 'react-helmet-async';
import { useEffect, useState, useMemo, useContext } from 'react';
// @mui
import {
  Stack,
  Card,
  Container,
  IconButton,
  Avatar,
  Typography,
  Button,
  Grid,
  Modal,
  Box,
  CircularProgress
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { id } from "date-fns/locale";
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from "@mui/icons-material/Close";
import { format, isValid, parseISO } from 'date-fns';
import MUIDataTable from "mui-datatables";
import { TableLoader } from '../../../components/table';
import AddPegawaiKeluarPage from './AddPegawaiKeluarPage';
import EditPegawaiKeluarPage from './EditPegawaiKeluarPage';
import DetailPegawaiKeluarPage from './DetailPegawaiKeluarPage';
import Iconify from '../../../components/iconify';
import TablePopover from './TablePopover';
import { getCurrentDate, customDate, dateDiff } from "../../../utils/formatDate";
import NotificationContext from '../../../context/notificationContext';
// API
import { axiosPrivate } from '../../../common/axiosPrivate';

export default function PegawaiPage() {
  const notificationCtx = useContext(NotificationContext);
  const [open, setOpen] = useState(null);
  const [data, setData] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [selectedId, setSelectedId] = useState();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState();
  const BodyComponent = useMemo(
    () => (props) => (
      <TableLoader loading={tableLoading} {...props} />
    ),
    [tableLoading]
  );

  const columns = [
    {
      name: "_id",
      options: {
        display: false,
        filter: false,
        sort: false
      }
    },
    {
      name: "number",
      label: "No",
      options: {
        filter: false,
        sort: false,
        customBodyRender : (value, tableMeta) => {
          const rowIndex = Number(tableMeta.rowIndex) + 1;
          return rowIndex;
        }
      },
    },
    {
     name: "nama",
     label: "Nama",
     options: {
      filter: false,
      sort: true,
      customBodyRender: (nama, meta) => {
        const profilUrlId = meta.rowData[16];
        const profilUrl = profilUrlId ? `https://drive.google.com/thumbnail?id=${profilUrlId}` : '';
        return (
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar alt={profilUrl} src={profilUrl} imgProps={{ referrerPolicy: "no-referrer" }} />
            <Typography variant="subtitle2" noWrap>
              {nama}
            </Typography>
          </Stack>
        )
      }
     }
    },
    {
      name: "kodePegawai",
      label: "Kode",
      options: {
       filter: false,
       sort: false,
      }
    },
    {
      name: "namaKantor",
      label: "Kantor",
      options: {
       filter: true,
       sort: true,
      }
     },
    {
     name: "namaJabatan",
     label: "Jabatan",
     options: {
      filter: true,
      sort: true,
     }
    },
    {
      name: "sebab",
      label: "Sebab",
      options: {
        filter: true,
        sort: true,
      }
    },
    {
    name: "keterangan",
    label: "Keterangan",
    options: {
        filter: true,
        sort: false,
      }
    },
    {
      name: "tglMasuk",
      label: "Tgl Masuk",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => value ? format(parseISO(value), 'dd/MM/yyyy') : ''
      }
    },
    {
      name: "tglKeluar",
      label: "Tgl Keluar",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const date = value ? new Date(value) : null;
          return date && isValid(date) ? format(date, 'dd/MM/yyyy') : '-';
        },
        filterType: 'custom',
        customFilterListOptions: {
          render: v => {
            if (v[0] && v[1]) {
              const startDate = format(new Date(v[0]), 'dd/MM/yyyy');
              const enDate = format(new Date(v[1]), 'dd/MM/yyyy');

              return [`Keluar Dari: ${startDate}`, `Sampai Dengan: ${enDate}`];
            } if (v[0]) {
              const startDate = format(new Date(v[0]), 'dd/MM/yyyy');

              return `Keluar Dari: ${startDate}`;
            } if (v[1]) {
              const enDate = format(new Date(v[1]), 'dd/MM/yyyy');

              return `Sampai Dengan: ${enDate}`;
            }
            return [];
          },
          update: (filterList, filterPos, index) => {
            if (filterPos === 0) {
              filterList[index].splice(filterPos, 1, '');
            } else if (filterPos === 1) {
              filterList[index].splice(filterPos, 1);
            } else if (filterPos === -1) {
              filterList[index] = [];
            }

            return filterList;
          },
        },
        filterOptions: {
          fullWidth: true,
          names: [],
          logic(date, filters) {
            const formated = new Date(customDate(date));

            if (filters[0] && filters[1]) {
              return !(formated >= filters[0] && formated <= filters[1]);
            }
            return false;
          },
          display: (filterList, onChange, index, column) => (
            <>
              <Grid container spacing={3} >
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider adapterLocale={id} dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Keluar Dari"
                      value={filterList[index][0] || null}
                      onChange={date => {
                        filterList[index][0] = date;
                        onChange(filterList[index], index, column);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'standard'
                        },
                        actionBar: { actions: ['clear'] },
                      }}
                    />
                </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider adapterLocale={id} dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Sampai Dengan"
                      value={filterList[index][1] || null}
                      onChange={date => {
                        filterList[index][1] = date;
                        onChange(filterList[index], index, column);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'standard'
                        },
                        actionBar: { actions: ['clear'] },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </>
          ),
        },
      }
    },
    {
      name: "tglMasuk",
      label: "Lama Kerja",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value, meta) => {
          const tglKeluar = meta.rowData[9];
          const masuk = value ? new Date(value) : null;
          const keluar = tglKeluar ? new Date(tglKeluar) : null;

          if (!isValid(masuk) || !isValid(keluar)) return '-';

          return getDateDiff(format(masuk, 'yyyy-MM-dd'), format(keluar, 'yyyy-MM-dd'));
        }
      }
    },
    {
      name: "tglStatus",
      label: "Tgl PKWTT",
      options: {
        filter: false,
        sort: false,
        display: false,
        customBodyRender: (value) => value ? format(parseISO(value), 'dd/MM/yyyy') : ''
      }
    },
    {
      name: "tglMulaiKontrak",
      label: "Tgl Mulai Kontrak",
      options: {
        filter: false,
        sort: false,
        display: false,
        customBodyRender: (value) => value ? format(parseISO(value), 'dd/MM/yyyy') : ''
      }
    },
    {
      name: "tglAkhirKontrak",
      label: "Tgl Akhir Kontrak",
      options: {
        filter: false,
        sort: false,
        display: false,
        customBodyRender: (value) => value ? format(parseISO(value), 'dd/MM/yyyy') : ''
      }
    },
    {
      name: "kontrakMagangKe",
      label: "Magang Ke",
      options: {
        filter: false,
        sort: true,
        display: false
      }
    },
    {
      name: "kontrakPkwtKe",
      label: "PKWT Ke",
      options: {
        filter: false,
        sort: true,
        display: false
      }
    },
    {
      name: "profilUrlId",
      label: "Google Drive Photo ID",
      options: {
        filter: false,
        sort: false,
        display: false,
        viewColumns: false
      }
    },
     // Hidden Columns
    {
      name: "tempatLahir",
      label: "Tempat Lahir",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "tglLahir",
      label: "Tgl Lahir",
      options: {
        filter: false,
        sort: false,
        display: false,
        customBodyRender: (value) => value ? format(parseISO(value), 'dd/MM/yyyy') : ''
      }
    },
    {
      name: "statusPerkawinan",
      label: "Status Perkawinan",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "pendidikan",
      label: "Pendidikan",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "agama",
      label: "Agama",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "ukuranBaju",
      label: "Ukuran Baju",
      options: {
        filter: false,
        sort: true,
        display: false
      }
    },
    {
      name: "kecamatan",
      label: "Kecamatan",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "kotaKabupaten",
      label: "Kota Kabupaten",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "alamat",
      label: "Alamat",
      options: {
        filter: false,
        sort: false,
        display: false,
        customBodyRender: (value) => value ? value.trim() : ''
      }
    },
    {
      name: "kecamatanDomisili",
      label: "Kecamatan Domisili",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "kotaKabupatenDomisili",
      label: "Kota Kabupaten Domisili",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "alamatDomisili",
      label: "Alamat Domisili",
      options: {
        filter: false,
        sort: false,
        display: false,
        customBodyRender: (value) => value ? value.trim() : ''
      }
    },
    {
      name: "noRekening",
      label: "No. Rekening",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "noHp",
      label: "No. Hp",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "noDarurat",
      label: "No. Darurat",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "nik",
      label: "NIK",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "npwp",
      label: "NPWP",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "bpjs",
      label: "BPJS",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "email",
      label: "Email",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "integritas",
      label: "Integritas",
      options: {
        filter: false,
        sort: false,
        display: false,
        customBodyRender: (value) => value ? 'Ya' : 'Tidak'
      }
    },
    {
      name: "idAnggota",
      label: "ID Anggota",
      options: {
       filter: false,
       sort: true,
       display: false
      }
    }, // End Hidden Columns
    {
      name: "profilUrlId",
      label: "Google Drive Photo ID",
      options: {
        filter: false,
        sort: false,
        display: false,
        viewColumns: false,
      }
     },
     {
      name: "_id",
      label: "Aksi",
      options: {
       filter: false,
       sort: false,
       customHeadRender: () => null,
       customBodyRender: (value) => (
        <IconButton size="small" color="inherit" onClick={(event) => handleOpenMenu(event, value)}>
          <Iconify icon={'eva:more-vertical-fill'} />
        </IconButton>
       )
      }
     }
   ];
   
  const options = {
    filterType: "dropdown",
    selectableRowsHideCheckboxes: true,
    customToolbarSelect: (selectedRows, displayData, setSelectedRows) => deleteLoading ? (
      <CircularProgress
        sx={{
          color: '#637381',
          mr: 2
        }}
        size={20}
      />
    ) : (
      <DeleteIcon
        size={20}
        sx={{
          color: '#637381',
          cursor: 'pointer',
          mr: 2
        }}
        onClick={() => handleDelete(selectedRows, displayData, setSelectedRows)}
      />
    ),
    downloadOptions: {
      filename: `pegawai-keluar-${getCurrentDate('-')}.csv`,
      filterOptions: {
        useDisplayedColumnsOnly: true,
        useDisplayedRowsOnly: true,
      }
    }
  };

  const getDateDiff = (startDate, endDate) => {
    const { years, months, days } = dateDiff(startDate, endDate);

    let result = '';
    if (years > 0) {
        result += `${years} Tahun `;
    }
    if (months > 0) {
        result += `${months} Bulan `;
    }
    if (days > 0) {
        result += `${days} Hari `;
    }

    return result.trim(); // Remove trailing space
  };

  const handleOpenMenu = (event, id) => {
    setOpen(event.currentTarget);
    setSelectedId(id);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleOpenModal = (content) => {
    setModalContent(content);
    setModalOpen(true);
  }

  const handleCloseModal = () => {
    setModalOpen(false);
  }

  const handleUpdateTable = (newData) => {
    const index = data.findIndex(item => item._id === newData._id);
  
    if (index !== -1) {
      data[index] = { ...data[index], ...newData };
      setData([...data]);
    } else {
      setData([newData, ...data]);
    }
  };

  const handleDelete = (selectedRows, displayData, setSelectedRows) => {
    setDeleteLoading(true);

    const ids = selectedRows.data.map(d => displayData[d.index].data[0]);

    axiosPrivate.post('/pegawai-keluar/destroy', {'data' : ids})
    .then((response) => {
      const count = response?.data?.deletedCount;

      if(count > 0) {
        ids.map((item) => setData(prev => prev.filter(e => e._id !== item)));
        setSelectedRows([]);
      }

      if(count > 0) {
        setData(prev => prev.filter(e => e._id !== ids))
      }

      handleMessage(response);
    })
    .catch((error) => handleMessage(error?.response))
    .finally(() => setDeleteLoading(false));
  }

  const handleMessage = async (params) => {
    const count = params?.data?.deletedCount;

    if(count > 0) {
      notificationCtx.setup({
        severity: 'success',
        title: 'Success',
        message: "Data Berhasil Dihapus."
      });
    } else {
      notificationCtx.setup({
        severity: 'error',
        title: 'Error',
        message: "Data Gagal Dihapus!"
      });
    }
  };

  const RenderFormContent = () => {
    switch (modalContent) {
      case 0:
        return <AddPegawaiKeluarPage
                  handleCloseModal={handleCloseModal}
                  handleUpdateTable={handleUpdateTable}
              />;
      case 1:
        return <EditPegawaiKeluarPage
                  id={selectedId}
                  handleCloseModal={handleCloseModal}
                  handleUpdateTable={handleUpdateTable}
                />;
      case 2:
        return <DetailPegawaiKeluarPage
                  id={selectedId}
                />;
      default:
        return <div>Not display anything</div>;
    }
  }

  // Fetch Api
  useEffect(() => {
    axiosPrivate.get('pegawai-keluar')
    .then((response) => {
      // console.log(response);
      
      setData(response?.data)
    })
    .catch((error) => console.log(error))
    .finally(() => setTableLoading(false))
  }, []);

  return (
    <>
      <Helmet>
        <title> List Pegawai | Sip App </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="end" sx={{ mb: 2 }}>
          <Button
            onClick={() => handleOpenModal(0)}
            variant='contained'
            startIcon={<Iconify icon="eva:plus-fill" />}
          >
            Tambah
          </Button>
        </Stack>
        <Card>
          <MUIDataTable
            title="List Pegawai Keluar"
            data={data}
            columns={columns}
            options={options}
            handleOpenMenu={handleOpenMenu}
            components={{ TableBody: BodyComponent }}
          />
        </Card>
      </Container>

      <TablePopover
        open={Boolean(open)}
        anchorEl={open}
        handleCloseMenu={handleCloseMenu}
        handleOpenModal={handleOpenModal}
      />

      <Modal
        sx={{
          overflowY: 'scroll',
          pt: 5,
          pb: 5
        }}
        hideBackdrop
        open={modalOpen}
        onClose={handleCloseModal}
        style={{ backgroundColor: "#fff", backdropFilter: "blur(0px)" }}
      >
        <>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              margin: 1
            }}
          >
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleCloseModal}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <RenderFormContent />
        </>
      </Modal>
    </>
  );
}
